import { Box, InputAdornment, Popover, Stack, TextField, Tooltip } from "@mui/material";
import { getShortcutHint } from "@src/appV2/Omnisearch/components/KeyboardHints";
import { useCallback, useEffect, useRef } from "react";
import { useLocalStorage } from "@src/appV2/lib/utils/useLocalstorage";
import { Avatar } from "@src/appV2/lib/Avatar";
import { Button } from "@src/appV2/redesign/components/Button";
import { IconButton } from "@src/appV2/redesign/components/IconButton";
import { useIsRebrand } from "@src/appV2/redesign/theme/RebrandProvider";

import { Icon } from "@src/appV2/redesign/components/Icon";
import { Text } from "@src/appV2/redesign/components/Text";
import { AccountSwitcherMenuWorkplaceDesktop } from "./AccountSwitcherDropdownWorkplace";
import { useAccountSwitcher } from "../../Shared/AccountSwitcher/useAccountSwitcher";
import { AccountSwitcherDropdownAdminDesktop } from "./AccountSwitcherDropdownAdmin";
import {
  type AdminSearchTabValue,
  ADMIN_SEARCH_TAB_STORAGE_KEY,
} from "../../Shared/AccountSwitcher/types";

export function AccountSwitcherDesktop() {
  const isRebrand = useIsRebrand();
  const anchorRef = useRef<HTMLDivElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const [selectedTab, setSelectedTab] = useLocalStorage<AdminSearchTabValue>(
    ADMIN_SEARCH_TAB_STORAGE_KEY,
    "all"
  );
  const {
    switcherTitle,
    switcherSubtitle,
    displayAdminSwitcher,
    dropdownVisible,
    setDropdownVisible,
    onAdminSearchBarEntered,
    onDropdownButtonClicked,
    onDropdownMouseLeave,
    workerDetails,
    workplaceDetails,
    onSearchChanged,
    onUserClicked,
    onAdminClicked,
    hasSearchTerm,
  } = useAccountSwitcher();

  const closeDropdown = useCallback(() => {
    onDropdownMouseLeave();
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
  }, [onDropdownMouseLeave]);

  // We disable the Popover backdrop (hideBackdrop + pointerEvents: "none" on
  // the Modal root) so that clicks pass through to the TextField, preserving
  // native text interactions like double-click-to-select. This listener
  // replaces the backdrop's "click outside to close" behavior.
  useEffect(() => {
    if (!dropdownVisible) {
      return;
    }

    function handleMouseDown({ target }: MouseEvent) {
      if (!(target instanceof Node)) {
        return;
      }

      const isInsideAnchor = anchorRef.current?.contains(target) ?? false;
      const isInsidePopover = popoverRef.current?.contains(target) ?? false;

      if (!isInsideAnchor && !isInsidePopover) {
        closeDropdown();
      }
    }

    document.addEventListener("mousedown", handleMouseDown);
    return () => {
      document.removeEventListener("mousedown", handleMouseDown);
    };
  }, [dropdownVisible, closeDropdown]);

  const isAllMode = selectedTab === "all" && hasSearchTerm;

  const getPopoverWidth = () => {
    if (!displayAdminSwitcher) {
      return anchorRef.current?.offsetWidth;
    }
    return isAllMode ? "700px" : "400px";
  };

  return (
    <Box ref={anchorRef}>
      {displayAdminSwitcher ? (
        <TextField
          fullWidth
          variant="outlined"
          size="small"
          placeholder={`Search agents or workplaces... (${getShortcutHint()})`}
          autoComplete="off"
          sx={(theme) => ({
            "& .MuiOutlinedInput-root": {
              backgroundColor: isRebrand
                ? theme.palette.background?.base
                : theme.palette.background?.inverted?.muted,
              color: isRebrand
                ? theme.palette.base?.base
                : theme.palette.navbar?.itemContentSelected,
              paddingRight: 0,
              "& fieldset": {
                borderColor: isRebrand
                  ? theme.palette.button?.outline?.normal?.borderColor
                  : theme.palette.button?.outline?.inverted?.normal?.borderColor,
              },
              "&:hover fieldset": {
                borderColor: isRebrand
                  ? theme.palette.button?.outline?.hover?.borderColor
                  : theme.palette.button?.outline?.inverted?.hover?.borderColor,
              },
              "&.Mui-focused fieldset": {
                borderColor: isRebrand
                  ? theme.palette.button?.outline?.hover?.borderColor
                  : theme.palette.button?.outline?.inverted?.hover?.borderColor,
              },
            },
            "& .MuiOutlinedInput-input": {
              color: isRebrand
                ? theme.palette.base?.base
                : theme.palette.navbar?.itemContentSelected,
              "&::placeholder": {
                color: isRebrand ? theme.palette.base?.muted : theme.palette.navbar?.itemContent,
                opacity: 1,
              },
            },
            width: "400px",
          })}
          onFocus={onAdminSearchBarEntered}
          onChange={(event) => {
            setDropdownVisible(true);
            onSearchChanged(event.target.value);
          }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end" sx={{ marginLeft: 0, paddingLeft: 0 }}>
                <Tooltip title="Switch To Admin">
                  <span>
                    <IconButton
                      variant={isRebrand ? "link" : "linkInverted"}
                      iconType="person-male-formal"
                      sx={(theme) => ({
                        borderRadius: 0,
                        borderLeft: isRebrand
                          ? `1px solid ${theme.palette.button?.outline?.normal?.borderColor}`
                          : `1px solid ${theme.palette.button?.outline?.inverted?.normal?.borderColor}`,
                        paddingX: 3,
                        height: "100%",
                        "&:hover": {
                          backgroundColor: isRebrand
                            ? theme.palette.background?.base
                            : theme.palette.background?.inverted?.muted,
                          ...(isRebrand && {
                            borderLeft: `1px solid ${theme.palette.button?.outline?.normal?.borderColor}`,
                          }),
                        },
                      })}
                      onClick={(e) => {
                        e.stopPropagation();
                        onAdminClicked();
                      }}
                    />
                  </span>
                </Tooltip>
              </InputAdornment>
            ),
          }}
        />
      ) : (
        <Tooltip
          title={switcherTitle}
          enterDelay={300}
          slotProps={{
            tooltip: {
              sx: {
                maxWidth: 400,
              },
            },
          }}
        >
          <span>
            <Button
              variant={isRebrand ? "link" : "linkInverted"}
              sx={{
                width: "fit-content",
                minWidth: "250px",
                maxWidth: "300px",
                minHeight: "50px",
              }}
              onClick={onDropdownButtonClicked}
            >
              <Stack direction="row" alignItems="center" gap={3} overflow="hidden" paddingY={1}>
                <Avatar
                  name={switcherTitle}
                  size="small"
                  sx={(theme) => ({
                    bgcolor: theme.palette.navbar?.avatarBackground,
                    color: theme.palette.navbar?.avatarText,
                    flexShrink: 0,
                  })}
                />
                <Stack direction="column" flex={1} minWidth={0}>
                  <Text
                    variant="body1"
                    textAlign="left"
                    color={(theme) =>
                      isRebrand ? theme.palette.base?.base : theme.palette.navbar?.userName
                    }
                    noWrap
                  >
                    {switcherTitle}
                  </Text>
                  {switcherSubtitle && (
                    <Text variant="caption" textAlign="left" noWrap>
                      {switcherSubtitle}
                    </Text>
                  )}
                </Stack>
                <Icon
                  type="caret-bottom"
                  sx={(theme) => ({
                    color: isRebrand
                      ? theme.palette.button?.outline.hover.color
                      : theme.palette.button?.outline.inverted.hover.color,
                    flexShrink: 0,
                  })}
                />
              </Stack>
            </Button>
          </span>
        </Tooltip>
      )}

      <Popover
        open={dropdownVisible}
        anchorEl={anchorRef.current}
        onClose={(_event, reason) => {
          if (reason === "escapeKeyDown") {
            closeDropdown();
          }
        }}
        disableAutoFocus
        disableEnforceFocus
        anchorOrigin={{
          vertical: "bottom",
          horizontal: displayAdminSwitcher && isAllMode ? "left" : "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: displayAdminSwitcher && isAllMode ? "left" : "right",
        }}
        // Let clicks pass through to the TextField for text selection.
        // The mousedown listener above handles outside-click-to-close instead.
        hideBackdrop
        sx={{ pointerEvents: "none" }}
        slotProps={{
          paper: {
            ref: popoverRef,
            sx: (theme) => ({
              pointerEvents: "auto",
              width: getPopoverWidth(),
              background: theme.palette.navbar?.background,
              boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.4)",
              paddingX: displayAdminSwitcher ? 1 : 3,
              paddingY: displayAdminSwitcher ? 0 : 3,
            }),
          },
        }}
      >
        {displayAdminSwitcher ? (
          <AccountSwitcherDropdownAdminDesktop
            workerDetails={workerDetails}
            workplaceDetails={workplaceDetails}
            onUserClicked={onUserClicked}
            hasSearchTerm={hasSearchTerm}
            selectedTab={selectedTab}
            onTabChange={setSelectedTab}
          />
        ) : (
          <AccountSwitcherMenuWorkplaceDesktop
            workplaceDetails={workplaceDetails}
            onUserClicked={onUserClicked}
          />
        )}
      </Popover>
    </Box>
  );
}
